import './apartment.scss';
import 'slick-carousel';

/* eslint-disable */
import * as THREE from 'three';
import Component from '../../components/component/component';

import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';

import glassMaterialData from './import/glass-material-grey.json';

export default class Apartment extends Component {
    static initSelector: string = '.apartment-3d-container';

    scene: THREE.Scene;
    renderer: THREE.WebGLRenderer;
    camera: THREE.PerspectiveCamera;
    controls: OrbitControls;
    glassMaterial: THREE.MeshPhysicalMaterial;
    raycaster: THREE.Raycaster;
    mouse: THREE.Vector2;
    orbitMouse: THREE.Vector2;
    sprite: THREE.Sprite;
    hoveringObject: null|ReturnType<typeof Object>;
    targetCameraPosition: THREE.Vector3;
    gsapMovement: GSAPTween;
    rotationTimer: number|null;

    constructor(target: HTMLElement) {
        super(target);

        this.init();
    }

    init() {
        this.orbitMouse = new THREE.Vector2();

        this.setupScene();
        this.setupRenderer();
        this.setupCameraAndControls();
        this.setupLighting();
        this.setupRaycaster();

        this.loadGlassMaterial();
        this.loadModelsAndMaterials();

        window.addEventListener('resize', () => this.onWindowResize());
    }

    setupScene(): void {
        this.scene = new THREE.Scene();
    }

    setupRenderer(): void {
        this.renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
        });
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.setSize(this.element.innerWidth(), this.element.innerHeight());
        this.renderer.setClearColor(0xffffff, 0);
        this.element.append(this.renderer.domElement);

        this.renderer.setAnimationLoop(() => this.animate());
        this.renderer.domElement.addEventListener('mousedown', () => this.element.addClass('apartment-3d-container--touched'));
        this.renderer.domElement.addEventListener('touchstart', () => this.element.addClass('apartment-3d-container--touched'));
    }

    setupCameraAndControls(): void {
        // Camera
        this.camera = new THREE.PerspectiveCamera(50, this.element.innerWidth() / this.element.innerHeight(), 0.1, 2000);
        this.camera.position.set(0, 0, 0);

        // Controls
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.maxPolarAngle = Math.PI / 2;
        this.controls.enablePan = false;
        this.controls.enableZoom = false;
        this.controls.autoRotate = false;
        this.controls.autoRotateSpeed = 0.35;
        this.controls.maxAzimuthalAngle = this.controls.getAzimuthalAngle()

        const rotatationTimeout = 3500;

        this.rotationTimer = window.setTimeout(() => {
            this.controls.autoRotate = true;
        }, rotatationTimeout);

        this.controls.addEventListener('start', () => {
            if (this.rotationTimer) {
                window.clearTimeout(this.rotationTimer);
                this.rotationTimer = null;
            }

            this.controls.autoRotate = false;
        });

        this.controls.addEventListener('end', () => {
            this.rotationTimer = window.setTimeout(() => {
                this.controls.autoRotate = true;
            }, rotatationTimeout);
        });
    }

    setupLighting(): void {
        const ambientLight: THREE.AmbientLight = new THREE.AmbientLight(0xffffff, 0.5);

        this.scene.add(ambientLight);

        const directionalLight: THREE.DirectionalLight = new THREE.DirectionalLight(0xffffff, 1);

        directionalLight.position.set(0, 1, 1).normalize();
        this.scene.add(directionalLight);
    }

    setupRaycaster(): void {
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();
    }

    loadGlassMaterial(): void {
        this.glassMaterial = new THREE.MeshPhysicalMaterial({
            color: glassMaterialData.color || 0xffffff,
            opacity: glassMaterialData.opacity || 0.5,
            transparent: glassMaterialData.transparent || true,
            roughness: 0,
            metalness: 0,
            clearcoat: 1,
            clearcoatRoughness: 0,
            reflectivity: glassMaterialData.reflectivity || 0.9,
            refractionRatio: 0.98,
            ior: 1.5,
        });
    }

    loadModelsAndMaterials(): void {
        const loader: GLTFLoader = new GLTFLoader();
        const dracoLoader: DRACOLoader = new DRACOLoader();

        dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/')
        loader.setDRACOLoader(dracoLoader)

        loader.load(
            this.element.data('model-url'),
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
            (gltf: any) => {
                const model: THREE.Group = gltf.scene;

                const box = new THREE.Box3().setFromObject(model)

                const boxCenter = box.getCenter(new THREE.Vector3())
                const boxSize = box.getSize(new THREE.Vector3())

                model.position.set(-boxCenter.x, boxSize.y / 2 - boxCenter.y, -boxCenter.z);

                this.scene.add(model);

                this.applyMaterialsToModel(model);
                this.adjustCameraToFitModel(model);
                this.hideLoaderShowCanvas();
            },
            (xhr: ProgressEvent) => this.updateLoadingBar(xhr),
        );
    }

    applyMaterialsToModel(model: THREE.Group): void {
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        model.traverse((child: any) => {
            if (child.isMesh && child.name.startsWith('GLASS')) {
                child.material = this.glassMaterial;
            } else if (child.name.startsWith('CENTERPOINT')) {
                const centerPosition = new THREE.Vector3();
                this.targetCameraPosition = child.getWorldPosition(centerPosition);

                child.visible = false;
            }
        });
    }

    // Adjust camera position and controls to focus on the loaded model
    adjustCameraToFitModel(model: THREE.Group): void {
        const box: THREE.Box3 = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        const distance: number = size.length()

        if (this.targetCameraPosition) {
            this.camera.position.set(this.targetCameraPosition.x, this.targetCameraPosition.y + (distance * 1.2), this.targetCameraPosition.z);
            this.camera.lookAt(this.targetCameraPosition);

            this.controls.target.copy(this.targetCameraPosition);
            this.controls.update();
        } else {
            this.camera.position.set(center.x, center.y + (distance * 1.2), center.z);
            this.camera.lookAt(center);

            this.controls.target.copy(center);
            this.controls.update();
        }
    }

    updateLoadingBar(xhr: ProgressEvent): void {
        //
    }

    // Hide loader and show canvas
    hideLoaderShowCanvas(): void {
        this.element.removeClass('apartment-3d-container--loading');
        this.renderer.domElement.style.display = 'block';
    }

    onWindowResize(): void {
        this.camera.aspect = this.element.innerWidth() / this.element.innerHeight();
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(this.element.innerWidth(), this.element.innerHeight());
    }

    animate(): void {
        this.controls.update();
        this.renderer.render(this.scene, this.camera);
    }
}

export class ApartmentNav extends Component {
    static initSelector: string = '#apartment-nav';

    private navItems: NodeListOf<HTMLLIElement>;
    private sections: Map<string, HTMLElement>;
    private observer: IntersectionObserver;
    private lastSection: HTMLElement | null = null;
    private lastScrollY: number = 0;
    private scrollHandler: () => void;
    private intersectingSections: Set<string>;

    constructor(target: HTMLElement) {
        super(target);
        this.init();
    }

    init(): void {
        this.navItems = this.element.get(0).querySelectorAll('.apartment-nav__item');
        this.sections = new Map();

        this.navItems.forEach((item: HTMLLIElement) => {
            const link = item.querySelector('.apartment-nav__link') as HTMLAnchorElement;
            if (link) {
                const href = link.getAttribute('href');
                if (href && href.startsWith('#')) {
                    const sectionId = href.substring(1);
                    const section = document.getElementById(sectionId);
                    if (section) {
                        this.sections.set(sectionId, section);
                    }
                }
            }
        });

        this.findLastSection();

        this.intersectingSections = new Set();
        this.setupObserver();
        this.setupScrollListener();
        this.setupClickHandlers();
    }

    setupClickHandlers(): void {
        this.navItems.forEach((item: HTMLLIElement) => {
            const link = item.querySelector('.apartment-nav__link') as HTMLAnchorElement;
            if (link) {
                link.addEventListener('click', (event: Event) => {
                    event.preventDefault();
                    const href = link.getAttribute('href');
                    if (href && href.startsWith('#')) {
                        const sectionId = href.substring(1);
                        const section = this.sections.get(sectionId);
                        if (section) {
                            const headerOffset = window.gotoAndPlay?.headerOffset || 0;
                            const sectionTop = section.getBoundingClientRect().top + window.scrollY - headerOffset;

                            window.scrollTo({
                                top: sectionTop,
                                behavior: 'smooth'
                            });
                        }
                    }
                });
            }
        });
    }

    findLastSection(): void {
        let maxTop = -Infinity;
        this.sections.forEach((section) => {
            const rect = section.getBoundingClientRect();
            const absoluteTop = rect.top + window.scrollY;
            if (absoluteTop > maxTop) {
                maxTop = absoluteTop;
                this.lastSection = section;
            }
        });
    }

    setupScrollListener(): void {
        this.lastScrollY = window.scrollY;

        this.scrollHandler = () => {
            this.checkNavVisibility();
        };

        window.addEventListener('scroll', this.scrollHandler, { passive: true });

        setTimeout(() => {
            this.checkNavVisibility();
        }, 100);
    }

    checkNavVisibility(): void {
        if (!this.lastSection) return;

        const lastSectionRect = this.lastSection.getBoundingClientRect();
        const lastSectionBottom = lastSectionRect.bottom;

        if (lastSectionBottom < 0) {
            this.element.addClass('nav-hidden');
        } else {
            this.element.removeClass('nav-hidden');
        }

        this.lastScrollY = window.scrollY;
    }

    setupObserver(): void {
        const options: IntersectionObserverInit = {
            root: null,
            rootMargin: '-20% 0px -60% 0px',
            threshold: 0,
        };

        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    this.intersectingSections.add(entry.target.id);
                } else {
                    this.intersectingSections.delete(entry.target.id);
                }
            });

            this.updateActiveNav();
        }, options);

        this.sections.forEach((section) => {
            this.observer.observe(section);
        });
    }

    updateActiveNav(): void {
        if (this.intersectingSections.size === 0) {
            return;
        }

        const activeSections = Array.from(this.intersectingSections)
            .map(id => this.sections.get(id))
            .filter((section): section is HTMLElement => !!section);

        activeSections.sort((a, b) => b.getBoundingClientRect().top - a.getBoundingClientRect().top);

        if (activeSections.length > 0) {
            this.setActiveNav(activeSections[0].id);
        }
    }

    setActiveNav(sectionId: string): void {
        this.navItems.forEach((item: HTMLLIElement) => {
            item.classList.remove('active');
        });

        this.navItems.forEach((item: HTMLLIElement) => {
            const link = item.querySelector('.apartment-nav__link') as HTMLAnchorElement;
            if (link && link.getAttribute('href') === `#${sectionId}`) {
                item.classList.add('active');
            }
        });
    }

    destroy(): void {
        if (this.observer) {
            this.observer.disconnect();
        }
        if (this.scrollHandler) {
            window.removeEventListener('scroll', this.scrollHandler);
        }
    }
}

export class ApartmentGallerySlider extends Component {
    static initSelector: string = '.apartment-gallery-slider';

    constructor(target: HTMLElement) {
        super(target);
        this.init();
    }

    init(): void {
        this.element.slick({
            slidesToShow: 1,
            slidesToScroll: 1,
            centerMode: true,
            centerPadding: '12%',
            infinite: true,
            arrows: false,
            dots: false
        });
    }
}

export class ApartmentSubtabs extends Component {
    static initSelector: string = '.apartment-subtabs';

    controls: JQuery<HTMLAnchorElement>;
    contents: JQuery<HTMLElement>;
    animationDuration: number = 300;

    constructor(target: HTMLElement) {
        super(target);
        this.init();
    }

    init(): void {
        this.controls = this.element.find('.js-subtabs-control') as JQuery<HTMLAnchorElement>;
        this.contents = this.element.find('.js-subtabs-content') as JQuery<HTMLElement>;

        this.controls.each((index: number, control: HTMLAnchorElement) => {
            $(control).on('click', (e: JQuery.ClickEvent) => {
                e.preventDefault();
                this.switchTab(control);
            });
        });
    }

    switchTab(control: HTMLAnchorElement): void {
        const targetId = control.getAttribute('href')?.replace('#', '');

        if (!targetId) return;

        const currentTab = this.element.find('.js-subtabs-content.is-open');
        const newTab = this.element.find(`#${targetId}`);

        if (currentTab.attr('id') === targetId) return;

        this.controls.each((index: number, ctrl: HTMLAnchorElement) => {
            $(ctrl).removeClass('is-current');
        });

        $(control).addClass('is-current');

        currentTab.stop().animate({
            opacity: 0,
        }, this.animationDuration, 'swing', () => {
            currentTab.removeClass('is-open').removeAttr('style');

            newTab.addClass('is-open').css({ opacity: 0 });
            newTab.stop().animate({
                opacity: 1,
            }, this.animationDuration, 'swing', () => {
                newTab.removeAttr('style');
            });
        });
    }
}

export class ApartmentFloorplanTabs extends Component {
    static initSelector: string = '.js-apartment-tabs';

    private floorplanContainer: HTMLElement | null;
    private leftContainer: HTMLElement | null;
    private tabControls: NodeListOf<HTMLAnchorElement>;

    constructor(target: HTMLElement) {
        super(target);
        this.init();
    }

    init(): void {
        const apartmentInfo = this.element.closest('.apartment-info');
        if (!apartmentInfo) return;

        this.floorplanContainer = apartmentInfo.get(0)?.querySelector('.js-floorplan-tabs-container') || null;
        this.leftContainer = apartmentInfo.get(0)?.querySelector('.apartment-info__left') || null;
        this.tabControls = this.element.get(0).querySelectorAll('.js-tabs-control');

        this.setupTabListeners();
        this.checkInitialState();
    }

    setupTabListeners(): void {
        this.tabControls.forEach((control: HTMLAnchorElement) => {
            control.addEventListener('click', () => {
                const href = control.getAttribute('href');
                this.handleTabChange(href === '#floorplan');
            });
        });

        $(document).on('tab.changed', () => {
            this.checkCurrentTab();
        });
    }

    checkInitialState(): void {
        const currentTab = this.element.find('.js-tabs-control.is-current');
        if (currentTab.length > 0) {
            const href = currentTab.attr('href');
            this.handleTabChange(href === '#floorplan');
        }
    }

    checkCurrentTab(): void {
        const currentTab = this.element.find('.js-tabs-control.is-current');
        if (currentTab.length > 0) {
            const href = currentTab.attr('href');
            this.handleTabChange(href === '#floorplan');
        }
    }

    handleTabChange(isFloorplan: boolean): void {
        if (isFloorplan) {
            this.leftContainer?.classList.add('fade-out');

            setTimeout(() => {
                this.leftContainer?.classList.add('floorplan-shown');
                this.leftContainer?.classList.remove('fade-out');
                this.floorplanContainer?.classList.add('is-visible');
                this.leftContainer?.classList.remove('no-floorplan-shown');
            }, 290);
        } else {

            setTimeout(() => {
                this.leftContainer?.classList.add('no-floorplan-shown');
                this.floorplanContainer?.classList.remove('is-visible');
                this.leftContainer?.classList.remove('floorplan-shown');
            }, 300);
        }
    }
}

export class ApartmentContactFooter extends Component {
    static initSelector: string = '.apartment-contact-footer';

    private premiumSection: HTMLElement | null;
    private scrollHandler: () => void;

    constructor(target: HTMLElement) {
        super(target);
        this.init();
    }

    init(): void {
        this.premiumSection = document.querySelector('.section.premium');

        this.scrollHandler = () => this.onScroll();
        window.addEventListener('scroll', this.scrollHandler, { passive: true });

        this.onScroll();
    }

    onScroll(): void {
        const scrollY = window.scrollY;
        const el = this.element.get(0);

        if (scrollY < 320) {
            el.classList.remove('shown');
            return;
        }

        if (this.premiumSection) {
            const premiumRect = this.premiumSection.getBoundingClientRect();

            if (premiumRect.top <= window.innerHeight * 0.5) {
                el.classList.remove('shown');
                return;
            }
        }

        el.classList.add('shown');
    }

    destroy(): void {
        if (this.scrollHandler) {
            window.removeEventListener('scroll', this.scrollHandler);
        }
    }
}

$(() => {
    const nav = document.querySelector(ApartmentNav.initSelector);
    if (nav) {
        new ApartmentNav(nav as HTMLElement);
    }

    const gallerySlider = document.querySelector(ApartmentGallerySlider.initSelector);
    if (gallerySlider) {
        new ApartmentGallerySlider(gallerySlider as HTMLElement);
    }

    document.querySelectorAll(ApartmentSubtabs.initSelector).forEach((subtabs) => {
        new ApartmentSubtabs(subtabs as HTMLElement);
    });

    document.querySelectorAll(ApartmentFloorplanTabs.initSelector).forEach((tabs) => {
        new ApartmentFloorplanTabs(tabs as HTMLElement);
    });

    const contactFooter = document.querySelector(ApartmentContactFooter.initSelector);
    if (contactFooter) {
        new ApartmentContactFooter(contactFooter as HTMLElement);
    }
});
